import cx from "classnames";
import React, { useEffect, useState } from "react";

import { Column, Container, Row } from "@gamedev-web/gamedev-sdk-core";

import * as commonStyles from "../../components/commonStyles/commonStyles.module.css";
import { useMinPageHeight } from "../../helpers/windowHelpers";
import { StreamingEuRequest, StreamingEuStep } from "./models";
import { AccessToEntitlementData } from "./StreamingEuForms/AccessToEntitlementData";
import { EmailVerify } from "./StreamingEuForms/EmailVerify";
import { ProviderInformation } from "./StreamingEuForms/ProviderInformation";
import { StreamingProviderLicense } from "./StreamingEuForms/StreamingProviderLicense";
import { TermsAndConditions } from "./StreamingEuForms/TermsAndConditions";
import { ThankYou } from "./StreamingEuForms/ThankYou";
import { UserEntitlementData } from "./StreamingEuForms/UserEntitlementData";
import * as styles from "./StreamingEuPage.module.css";

export type StreamingEuPageProps = Readonly<{
  initialStep?: StreamingEuStep;
}>;

const StreamingEuPage = ({ initialStep }: StreamingEuPageProps) => {
  const minPageHeight = useMinPageHeight();
  const [currentPageStep, setCurrentPageStep] = useState(
    initialStep ?? StreamingEuStep.ProviderLicense
  );
  const [streamingData, setStreamingData] = useState({} as Partial<StreamingEuRequest>);
  const [isLoading, setIsLoading] = useState(true);

  const navigateToStep = (step: StreamingEuStep) => setCurrentPageStep(step);

  const storeStreamingData = (partialData: Partial<StreamingEuRequest>) =>
    setStreamingData({ ...streamingData, ...partialData });

  let stepContent = null;
  switch (currentPageStep) {
    case StreamingEuStep.ProviderLicense:
    default:
      stepContent = (
        <StreamingProviderLicense
          navigateToStep={navigateToStep}
          storeStreamingData={storeStreamingData}
        />
      );
      break;
    case StreamingEuStep.UserEntitlementData:
      stepContent = (
        <UserEntitlementData
          navigateToStep={navigateToStep}
          storeStreamingData={storeStreamingData}
        />
      );
      break;
    case StreamingEuStep.ProviderInformationMandatory:
      stepContent = (
        <ProviderInformation
          navigateToStep={navigateToStep}
          storeStreamingData={storeStreamingData}
          isMandatory={true}
        />
      );
      break;
    case StreamingEuStep.ProviderInformationOptional:
      stepContent = (
        <ProviderInformation
          navigateToStep={navigateToStep}
          storeStreamingData={storeStreamingData}
          isMandatory={false}
        />
      );
      break;
    case StreamingEuStep.AccessToEntitlementData:
      stepContent = (
        <AccessToEntitlementData
          navigateToStep={navigateToStep}
          storeStreamingData={storeStreamingData}
        />
      );
      break;
    case StreamingEuStep.TermsAndConditions:
      stepContent = (
        <TermsAndConditions
          navigateToStep={navigateToStep}
          storeStreamingData={storeStreamingData}
        />
      );
      break;
    case StreamingEuStep.ThankYou:
      stepContent = <ThankYou streamingData={streamingData as StreamingEuRequest} />;
      break;
    case StreamingEuStep.EmailVerify:
      stepContent = <EmailVerify />;
      break;
  }

  useEffect(() => {
    setIsLoading(false);
  }, []);

  return (
    <Container
      className={cx(commonStyles.pageContainer, styles.container)}
      style={{ minHeight: minPageHeight }}
    >
      {!isLoading && (
        <Row className={styles.contentBox}>
          <Column className={styles.innerContentBox}>{stepContent}</Column>
        </Row>
      )}
    </Container>
  );
};

export default StreamingEuPage;
